import { RefObject } from 'react';
import html2canvas from 'html2canvas';
import { ReactElement } from 'react';

interface UseIDCardDownloadParams {
  cardRef: RefObject<HTMLDivElement | null>;
  renderCardForPlan: (side?: 'front' | 'back') => ReactElement;
  hasBackSide: boolean;
}

export const useIDCardDownload = ({
  cardRef,
  renderCardForPlan,
  hasBackSide,
}: UseIDCardDownloadParams) => {
  const handleDownload = async () => {
    if (!cardRef.current) return;

    try {
      // Создаём временные контейнеры для обеих сторон
      const tempContainer = document.createElement('div');
      tempContainer.style.position = 'absolute';
      tempContainer.style.left = '-9999px';
      tempContainer.style.top = '-9999px';
      tempContainer.style.width = cardRef.current.offsetWidth + 'px';
      tempContainer.style.height = cardRef.current.offsetHeight + 'px';
      document.body.appendChild(tempContainer);

      // Создаём контейнеры для front и back
      const frontContainer = document.createElement('div');
      const backContainer = document.createElement('div');
      tempContainer.appendChild(frontContainer);
      tempContainer.appendChild(backContainer);

      // Используем ReactDOM для рендеринга обеих сторон
      const { createRoot } = await import('react-dom/client');
      
      // Рендерим front сторону
      const frontCard = renderCardForPlan('front');
      const frontRoot = createRoot(frontContainer);
      frontRoot.render(frontCard);

      // Ждём немного для рендеринга
      await new Promise(resolve => setTimeout(resolve, 200));

      let frontCanvas: HTMLCanvasElement | null = null;
      let backCanvas: HTMLCanvasElement | null = null;
      let backRoot: any = null;

      // Делаем скриншот front стороны
      try {
        const frontElement = frontContainer.firstChild as HTMLElement;
        if (frontElement) {
          frontCanvas = await html2canvas(frontElement, {
            scale: 2,
            backgroundColor: '#ffffff',
            useCORS: true,
            logging: false,
          });
        }
      } catch (error) {
        console.error('Error capturing front:', error);
      }

      // Если есть обратная сторона, делаем скриншот back
      if (hasBackSide) {
        const backCard = renderCardForPlan('back');
        backRoot = createRoot(backContainer);
        backRoot.render(backCard);
        
        await new Promise(resolve => setTimeout(resolve, 200));
        
        try {
          const backElement = backContainer.firstChild as HTMLElement;
          if (backElement) {
            backCanvas = await html2canvas(backElement, {
              scale: 2,
              backgroundColor: '#ffffff',
              useCORS: true,
              logging: false,
            });
          }
        } catch (error) {
          console.error('Error capturing back:', error);
        }
      }

      // Создаём общий canvas 1700x2200
      const finalCanvas = document.createElement('canvas');
      finalCanvas.width = 1700;
      finalCanvas.height = 2200;
      const ctx = finalCanvas.getContext('2d');
      
      if (!ctx) {
        document.body.removeChild(tempContainer);
        return;
      }

      // Заполняем белым
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, 1700, 2200);

      // Рисуем Front сторону
      if (frontCanvas) {
        const frontHeight = Math.min(frontCanvas.height, 900);
        const frontWidth = (frontCanvas.width / frontCanvas.height) * frontHeight;
        const frontX = (1700 - frontWidth) / 2;
        const frontY = 100;

        // Подпись "Front"
        ctx.fillStyle = '#000000';
        ctx.font = 'bold 32px Arial';
        ctx.textAlign = 'center';
        ctx.fillText('Front', 850, 60);

        // Рисуем front карту
        ctx.drawImage(frontCanvas, frontX, frontY, frontWidth, frontHeight);
      }

      // Рисуем Back сторону
      if (backCanvas) {
        const backHeight = Math.min(backCanvas.height, 900);
        const backWidth = (backCanvas.width / backCanvas.height) * backHeight;
        const backX = (1700 - backWidth) / 2;
        const backY = 1200;

        // Подпись "Back"
        ctx.fillStyle = '#000000';
        ctx.font = 'bold 32px Arial';
        ctx.textAlign = 'center';
        ctx.fillText('Back', 850, 1160);

        // Рисуем back карту
        ctx.drawImage(backCanvas, backX, backY, backWidth, backHeight);
      } else if (frontCanvas) {
        // Если нет back стороны, дублируем front
        const frontHeight = Math.min(frontCanvas.height, 900);
        const frontWidth = (frontCanvas.width / frontCanvas.height) * frontHeight;
        const frontX = (1700 - frontWidth) / 2;
        const backY = 1200;

        // Подпись "Back"
        ctx.fillStyle = '#000000';
        ctx.font = 'bold 32px Arial';
        ctx.textAlign = 'center';
        ctx.fillText('Back', 850, 1160);

        // Рисуем front карту как back
        ctx.drawImage(frontCanvas, frontX, backY, frontWidth, frontHeight);
      }

      // Очищаем временные элементы
      frontRoot.unmount();
      if (backRoot) {
        backRoot.unmount();
      }
      document.body.removeChild(tempContainer);

      // Скачиваем изображение
      const dataUrl = finalCanvas.toDataURL('image/png');
      const link = document.createElement('a');
      link.download = 'id-card.png';
      link.href = dataUrl;
      link.click();
    } catch (error) {
      console.error('Error generating image:', error);
    }
  };

  const handleDownloadEquitable = async () => {
    if (!cardRef.current) return;

    try {
      // Создаём временные контейнеры для обеих сторон
      const tempContainer = document.createElement('div');
      tempContainer.style.position = 'absolute';
      tempContainer.style.left = '-9999px';
      tempContainer.style.top = '-9999px';
      tempContainer.style.width = cardRef.current.offsetWidth + 'px';
      tempContainer.style.height = cardRef.current.offsetHeight + 'px';
      document.body.appendChild(tempContainer);

      // Создаём контейнеры для front и back
      const frontContainer = document.createElement('div');
      const backContainer = document.createElement('div');
      tempContainer.appendChild(frontContainer);
      tempContainer.appendChild(backContainer);

      // Используем ReactDOM для рендеринга обеих сторон
      const { createRoot } = await import('react-dom/client');
      
      // Рендерим front сторону
      const frontCard = renderCardForPlan('front');
      const frontRoot = createRoot(frontContainer);
      frontRoot.render(frontCard);

      // Ждём немного для рендеринга
      await new Promise(resolve => setTimeout(resolve, 200));

      let frontCanvas: HTMLCanvasElement | null = null;
      let backCanvas: HTMLCanvasElement | null = null;
      let backRoot: any = null;

      // Делаем скриншот front стороны
      try {
        const frontElement = frontContainer.firstChild as HTMLElement;
        if (frontElement) {
          frontCanvas = await html2canvas(frontElement, {
            scale: 2,
            backgroundColor: '#ffffff',
            useCORS: true,
            logging: false,
          });
        }
      } catch (error) {
        console.error('Error capturing front:', error);
      }

      // Рендерим back сторону
      if (hasBackSide) {
        const backCard = renderCardForPlan('back');
        backRoot = createRoot(backContainer);
        backRoot.render(backCard);
        
        await new Promise(resolve => setTimeout(resolve, 200));
        
        try {
          const backElement = backContainer.firstChild as HTMLElement;
          if (backElement) {
            backCanvas = await html2canvas(backElement, {
              scale: 2,
              backgroundColor: '#ffffff',
              useCORS: true,
              logging: false,
            });
          }
        } catch (error) {
          console.error('Error capturing back:', error);
        }
      }

      // Загружаем шаблон изображения
      const templateImage = new Image();
      templateImage.crossOrigin = 'anonymous';
      
      await new Promise<void>((resolve, reject) => {
        templateImage.onload = () => resolve();
        templateImage.onerror = reject;
        templateImage.src = '/images/equitable-template.png';
      });

      // Создаём canvas с размерами шаблона
      const finalCanvas = document.createElement('canvas');
      finalCanvas.width = templateImage.width;
      finalCanvas.height = templateImage.height;
      const ctx = finalCanvas.getContext('2d');
      
      if (!ctx) {
        document.body.removeChild(tempContainer);
        return;
      }

      // Рисуем шаблон как фон
      ctx.drawImage(templateImage, 0, 0);

      // Отступ сверху для карточек
      const topOffset = 1600;
      
      // Фиксированные размеры карточек
      const cardWidth = 734;
      const cardHeight = 422;

      // Рисуем Front карточку слева
      if (frontCanvas) {
        const leftX = 100; // Отступ слева
        const leftY = topOffset;

        ctx.drawImage(frontCanvas, leftX, leftY, cardWidth, cardHeight);
      }

      // Рисуем Back карточку справа
      if (backCanvas) {
        const rightX = finalCanvas.width - cardWidth - 100; // Отступ справа
        const rightY = topOffset;

        ctx.drawImage(backCanvas, rightX, rightY, cardWidth, cardHeight);
      } else if (frontCanvas) {
        // Если нет back стороны, дублируем front справа
        const rightX = finalCanvas.width - cardWidth - 100;
        const rightY = topOffset;

        ctx.drawImage(frontCanvas, rightX, rightY, cardWidth, cardHeight);
      }

      // Очищаем временные элементы
      frontRoot.unmount();
      if (backRoot) {
        backRoot.unmount();
      }
      document.body.removeChild(tempContainer);

      // Скачиваем изображение
      const dataUrl = finalCanvas.toDataURL('image/png');
      const link = document.createElement('a');
      link.download = 'id-card-equitable.png';
      link.href = dataUrl;
      link.click();
    } catch (error) {
      console.error('Error generating equitable image:', error);
    }
  };

  const handleDownloadGuardian = async () => {
    if (!cardRef.current) return;

    try {
      // Создаём временный контейнер для карточки
      const tempContainer = document.createElement('div');
      tempContainer.style.position = 'absolute';
      tempContainer.style.left = '-9999px';
      tempContainer.style.top = '-9999px';
      tempContainer.style.width = cardRef.current.offsetWidth + 'px';
      tempContainer.style.height = cardRef.current.offsetHeight + 'px';
      document.body.appendChild(tempContainer);

      // Создаём контейнер для карточки
      const cardContainer = document.createElement('div');
      tempContainer.appendChild(cardContainer);

      // Используем ReactDOM для рендеринга карточки
      const { createRoot } = await import('react-dom/client');
      
      // Рендерим карточку
      const card = renderCardForPlan();
      const cardRoot = createRoot(cardContainer);
      cardRoot.render(card);

      // Ждём немного для рендеринга
      await new Promise(resolve => setTimeout(resolve, 200));

      let cardCanvas: HTMLCanvasElement | null = null;

      // Делаем скриншот карточки
      try {
        const cardElement = cardContainer.firstChild as HTMLElement;
        if (cardElement) {
          cardCanvas = await html2canvas(cardElement, {
            scale: 2,
            backgroundColor: '#ffffff',
            useCORS: true,
            logging: false,
          });
        }
      } catch (error) {
        console.error('Error capturing card:', error);
      }

      // Загружаем шаблон изображения
      const templateImage = new Image();
      templateImage.crossOrigin = 'anonymous';
      
      await new Promise<void>((resolve, reject) => {
        templateImage.onload = () => resolve();
        templateImage.onerror = reject;
        templateImage.src = '/images/guardian-template.png';
      });

      // Создаём canvas с размерами шаблона
      const finalCanvas = document.createElement('canvas');
      finalCanvas.width = templateImage.width;
      finalCanvas.height = templateImage.height;
      const ctx = finalCanvas.getContext('2d');
      
      if (!ctx) {
        document.body.removeChild(tempContainer);
        return;
      }

      // Рисуем шаблон как фон
      ctx.drawImage(templateImage, 0, 0);

      // Фиксированные размеры и позиция карточки
      // const cardWidth = 886;
      // const cardHeight = 512;
      const leftX = 67;
      const topY = 140;

      // Рисуем карточку
      if (cardCanvas) {
        ctx.drawImage(cardCanvas, leftX, topY);
      }

      // Очищаем временные элементы
      cardRoot.unmount();
      document.body.removeChild(tempContainer);

      // Скачиваем изображение
      const dataUrl = finalCanvas.toDataURL('image/png');
      const link = document.createElement('a');
      link.download = 'id-card-guardian.png';
      link.href = dataUrl;
      link.click();
    } catch (error) {
      console.error('Error generating guardian image:', error);
    }
  };

  const openPrintWindow = (dataUrl: string) => {
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Print ID Card</title>
          <style>
            * {
              margin: 0;
              padding: 0;
              box-sizing: border-box;
            }
            body {
              display: flex;
              justify-content: center;
              align-items: center;
              min-height: 100vh;
              background: white;
            }
            img {
              max-width: 100%;
              height: auto;
              display: block;
            }
          </style>
        </head>
        <body>
          <img src="${dataUrl}" alt="ID Card" />
          <script>
            window.onload = function() {
              window.print();
              window.onafterprint = function() {
                window.close();
              };
            };
          </script>
        </body>
      </html>
    `);
    printWindow.document.close();
  };

  const handlePrint = async () => {
    if (!cardRef.current) return;

    try {
      const canvas = await html2canvas(cardRef.current, {
        scale: 2,
        backgroundColor: '#ffffff',
        useCORS: true,
        logging: false,
      });

      const dataUrl = canvas.toDataURL('image/png');
      openPrintWindow(dataUrl);
    } catch (error) {
      console.error('Error generating print image:', error);
    }
  };

  const handlePrintEquitable = async () => {
    if (!cardRef.current) return;

    try {
      // Создаём временные контейнеры для обеих сторон
      const tempContainer = document.createElement('div');
      tempContainer.style.position = 'absolute';
      tempContainer.style.left = '-9999px';
      tempContainer.style.top = '-9999px';
      tempContainer.style.width = cardRef.current.offsetWidth + 'px';
      tempContainer.style.height = cardRef.current.offsetHeight + 'px';
      document.body.appendChild(tempContainer);

      // Создаём контейнеры для front и back
      const frontContainer = document.createElement('div');
      const backContainer = document.createElement('div');
      tempContainer.appendChild(frontContainer);
      tempContainer.appendChild(backContainer);

      // Используем ReactDOM для рендеринга обеих сторон
      const { createRoot } = await import('react-dom/client');
      
      // Рендерим front сторону
      const frontCard = renderCardForPlan('front');
      const frontRoot = createRoot(frontContainer);
      frontRoot.render(frontCard);

      // Ждём немного для рендеринга
      await new Promise(resolve => setTimeout(resolve, 200));

      let frontCanvas: HTMLCanvasElement | null = null;
      let backCanvas: HTMLCanvasElement | null = null;
      let backRoot: any = null;

      // Делаем скриншот front стороны
      try {
        const frontElement = frontContainer.firstChild as HTMLElement;
        if (frontElement) {
          frontCanvas = await html2canvas(frontElement, {
            scale: 2,
            backgroundColor: '#ffffff',
            useCORS: true,
            logging: false,
          });
        }
      } catch (error) {
        console.error('Error capturing front:', error);
      }

      // Рендерим back сторону
      if (hasBackSide) {
        const backCard = renderCardForPlan('back');
        backRoot = createRoot(backContainer);
        backRoot.render(backCard);
        
        await new Promise(resolve => setTimeout(resolve, 200));
        
        try {
          const backElement = backContainer.firstChild as HTMLElement;
          if (backElement) {
            backCanvas = await html2canvas(backElement, {
              scale: 2,
              backgroundColor: '#ffffff',
              useCORS: true,
              logging: false,
            });
          }
        } catch (error) {
          console.error('Error capturing back:', error);
        }
      }

      // Загружаем шаблон изображения
      const templateImage = new Image();
      templateImage.crossOrigin = 'anonymous';
      
      await new Promise<void>((resolve, reject) => {
        templateImage.onload = () => resolve();
        templateImage.onerror = reject;
        templateImage.src = '/images/equitable-template.png';
      });

      // Создаём canvas с размерами шаблона
      const finalCanvas = document.createElement('canvas');
      finalCanvas.width = templateImage.width;
      finalCanvas.height = templateImage.height;
      const ctx = finalCanvas.getContext('2d');
      
      if (!ctx) {
        document.body.removeChild(tempContainer);
        return;
      }

      // Рисуем шаблон как фон
      ctx.drawImage(templateImage, 0, 0);

      // Отступ сверху для карточек
      const topOffset = 1600;
      
      // Фиксированные размеры карточек
      const cardWidth = 734;
      const cardHeight = 422;

      // Рисуем Front карточку слева
      if (frontCanvas) {
        const leftX = 100; // Отступ слева
        const leftY = topOffset;

        ctx.drawImage(frontCanvas, leftX, leftY, cardWidth, cardHeight);
      }

      // Рисуем Back карточку справа
      if (backCanvas) {
        const rightX = finalCanvas.width - cardWidth - 100; // Отступ справа
        const rightY = topOffset;

        ctx.drawImage(backCanvas, rightX, rightY, cardWidth, cardHeight);
      } else if (frontCanvas) {
        // Если нет back стороны, дублируем front справа
        const rightX = finalCanvas.width - cardWidth - 100;
        const rightY = topOffset;

        ctx.drawImage(frontCanvas, rightX, rightY, cardWidth, cardHeight);
      }

      // Очищаем временные элементы
      frontRoot.unmount();
      if (backRoot) {
        backRoot.unmount();
      }
      document.body.removeChild(tempContainer);

      // Открываем окно для печати
      const dataUrl = finalCanvas.toDataURL('image/png');
      openPrintWindow(dataUrl);
    } catch (error) {
      console.error('Error generating equitable print image:', error);
    }
  };

  const handleDownloadCigna = async () => {
    // Cigna использует тот же подход что и обычный handleDownload
    return handleDownload();
  };

  const handlePrintCigna = async () => {
    if (!cardRef.current) return;

    try {
      // Создаём временные контейнеры для обеих сторон
      const tempContainer = document.createElement('div');
      tempContainer.style.position = 'absolute';
      tempContainer.style.left = '-9999px';
      tempContainer.style.top = '-9999px';
      tempContainer.style.width = cardRef.current.offsetWidth + 'px';
      tempContainer.style.height = cardRef.current.offsetHeight + 'px';
      document.body.appendChild(tempContainer);

      // Создаём контейнеры для front и back
      const frontContainer = document.createElement('div');
      const backContainer = document.createElement('div');
      tempContainer.appendChild(frontContainer);
      tempContainer.appendChild(backContainer);

      // Используем ReactDOM для рендеринга обеих сторон
      const { createRoot } = await import('react-dom/client');
      
      // Рендерим front сторону
      const frontCard = renderCardForPlan('front');
      const frontRoot = createRoot(frontContainer);
      frontRoot.render(frontCard);

      // Ждём немного для рендеринга
      await new Promise(resolve => setTimeout(resolve, 200));

      let frontCanvas: HTMLCanvasElement | null = null;
      let backCanvas: HTMLCanvasElement | null = null;
      let backRoot: any = null;

      // Делаем скриншот front стороны
      try {
        const frontElement = frontContainer.firstChild as HTMLElement;
        if (frontElement) {
          frontCanvas = await html2canvas(frontElement, {
            scale: 2,
            backgroundColor: '#ffffff',
            useCORS: true,
            logging: false,
          });
        }
      } catch (error) {
        console.error('Error capturing front:', error);
      }

      // Если есть обратная сторона, делаем скриншот back
      if (hasBackSide) {
        const backCard = renderCardForPlan('back');
        backRoot = createRoot(backContainer);
        backRoot.render(backCard);
        
        await new Promise(resolve => setTimeout(resolve, 200));
        
        try {
          const backElement = backContainer.firstChild as HTMLElement;
          if (backElement) {
            backCanvas = await html2canvas(backElement, {
              scale: 2,
              backgroundColor: '#ffffff',
              useCORS: true,
              logging: false,
            });
          }
        } catch (error) {
          console.error('Error capturing back:', error);
        }
      }

      // Создаём общий canvas 1700x2200
      const finalCanvas = document.createElement('canvas');
      finalCanvas.width = 1700;
      finalCanvas.height = 2200;
      const ctx = finalCanvas.getContext('2d');
      
      if (!ctx) {
        document.body.removeChild(tempContainer);
        return;
      }

      // Заполняем белым
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, 1700, 2200);

      // Рисуем Front сторону
      if (frontCanvas) {
        const frontHeight = Math.min(frontCanvas.height, 900);
        const frontWidth = (frontCanvas.width / frontCanvas.height) * frontHeight;
        const frontX = (1700 - frontWidth) / 2;
        const frontY = 100;

        // Подпись "Front"
        ctx.fillStyle = '#000000';
        ctx.font = 'bold 32px Arial';
        ctx.textAlign = 'center';
        ctx.fillText('Front', 850, 60);

        // Рисуем front карту
        ctx.drawImage(frontCanvas, frontX, frontY, frontWidth, frontHeight);
      }

      // Рисуем Back сторону
      if (backCanvas) {
        const backHeight = Math.min(backCanvas.height, 900);
        const backWidth = (backCanvas.width / backCanvas.height) * backHeight;
        const backX = (1700 - backWidth) / 2;
        const backY = 1200;

        // Подпись "Back"
        ctx.fillStyle = '#000000';
        ctx.font = 'bold 32px Arial';
        ctx.textAlign = 'center';
        ctx.fillText('Back', 850, 1160);

        // Рисуем back карту
        ctx.drawImage(backCanvas, backX, backY, backWidth, backHeight);
      } else if (frontCanvas) {
        // Если нет back стороны, дублируем front
        const frontHeight = Math.min(frontCanvas.height, 900);
        const frontWidth = (frontCanvas.width / frontCanvas.height) * frontHeight;
        const frontX = (1700 - frontWidth) / 2;
        const backY = 1200;

        // Подпись "Back"
        ctx.fillStyle = '#000000';
        ctx.font = 'bold 32px Arial';
        ctx.textAlign = 'center';
        ctx.fillText('Back', 850, 1160);

        // Рисуем front карту как back
        ctx.drawImage(frontCanvas, frontX, backY, frontWidth, frontHeight);
      }

      // Очищаем временные элементы
      frontRoot.unmount();
      if (backRoot) {
        backRoot.unmount();
      }
      document.body.removeChild(tempContainer);

      // Открываем окно для печати
      const dataUrl = finalCanvas.toDataURL('image/png');
      openPrintWindow(dataUrl);
    } catch (error) {
      console.error('Error generating cigna print image:', error);
    }
  };

  const handlePrintGuardian = async () => {
    if (!cardRef.current) return;

    try {
      // Создаём временный контейнер для карточки
      const tempContainer = document.createElement('div');
      tempContainer.style.position = 'absolute';
      tempContainer.style.left = '-9999px';
      tempContainer.style.top = '-9999px';
      tempContainer.style.width = cardRef.current.offsetWidth + 'px';
      tempContainer.style.height = cardRef.current.offsetHeight + 'px';
      document.body.appendChild(tempContainer);

      // Создаём контейнер для карточки
      const cardContainer = document.createElement('div');
      tempContainer.appendChild(cardContainer);

      // Используем ReactDOM для рендеринга карточки
      const { createRoot } = await import('react-dom/client');
      
      // Рендерим карточку
      const card = renderCardForPlan();
      const cardRoot = createRoot(cardContainer);
      cardRoot.render(card);

      // Ждём немного для рендеринга
      await new Promise(resolve => setTimeout(resolve, 200));

      let cardCanvas: HTMLCanvasElement | null = null;

      // Делаем скриншот карточки
      try {
        const cardElement = cardContainer.firstChild as HTMLElement;
        if (cardElement) {
          cardCanvas = await html2canvas(cardElement, {
            scale: 2,
            backgroundColor: '#ffffff',
            useCORS: true,
            logging: false,
          });
        }
      } catch (error) {
        console.error('Error capturing card:', error);
      }

      // Загружаем шаблон изображения
      const templateImage = new Image();
      templateImage.crossOrigin = 'anonymous';
      
      await new Promise<void>((resolve, reject) => {
        templateImage.onload = () => resolve();
        templateImage.onerror = reject;
        templateImage.src = '/images/guardian-template.png';
      });

      // Создаём canvas с размерами шаблона
      const finalCanvas = document.createElement('canvas');
      finalCanvas.width = templateImage.width;
      finalCanvas.height = templateImage.height;
      const ctx = finalCanvas.getContext('2d');
      
      if (!ctx) {
        document.body.removeChild(tempContainer);
        return;
      }

      // Рисуем шаблон как фон
      ctx.drawImage(templateImage, 0, 0);

      // Фиксированные размеры и позиция карточки
      // const cardWidth = 886;
      // const cardHeight = 512;
      const leftX = 67;
      const topY = 140;

      // Рисуем карточку
      if (cardCanvas) {
        ctx.drawImage(cardCanvas, leftX, topY);
      }

      // Очищаем временные элементы
      cardRoot.unmount();
      document.body.removeChild(tempContainer);

      // Открываем окно для печати
      const dataUrl = finalCanvas.toDataURL('image/png');
      openPrintWindow(dataUrl);
    } catch (error) {
      console.error('Error generating guardian print image:', error);
    }
  };

  return { 
    handleDownload, 
    handleDownloadEquitable, 
    handleDownloadGuardian,
    handleDownloadCigna,
    handlePrint,
    handlePrintEquitable,
    handlePrintGuardian,
    handlePrintCigna,
  };
};

